import { AIType, DatasetsInfo, RobotType } from '@mygpt/app/datasets/services';
import { Http, PageQuery } from '@mygpt/app/http';
import { ActiveAddOnSubscription, ActivePlanSubscription } from '@mygpt/app/users/services';
import type { RealtimeProvider } from '@mygpt/app/chat/types/realtime';
import { requireSharedIntegration } from '@mygpt/app/config/integrations';

export interface GetRobotParams {
  aiId: string;
}

export type AIStatus =
  | 'init'
  | 'ready'
  | 'frozen' // frozen: 冻结
  | 'cloning_source'
  | 'cloning_target'
  | 'cloning_failed';

export type AIModel = 'file' | 'website' | 'agent';

interface UserConfig {
  key: string;
  value: string;
}

interface RobotInfoUrl {
  created_at: string;
  deleted_at: null;
  failed_reason: null;
  id: string;
  metadata: null;
  status: string;
  updated_at: string;
  url: string;
}

interface QuestionParam {
  id: string;
  question: string;
  question_type: string;
  answer: string;
}

/**
 * 超级管理员对该用户的配置覆写；未覆写为 null。
 * 后端 key 白名单见 felo-mygpt:mygpt/endpoints/user_quota.py:ALLOWED_OVERRIDE_KEYS
 * 通过 User model 字段自动序列化到 /users/me 响应。
 */
export interface AdminOverrides {
  /** 单文件 / 单次累计上传大小上限（MB），覆盖系统默认 */
  max_file_size_mb?: number;
}

export interface UserInfo {
  id: string;
  email: string;
  locale: string;
  name: string;
  picture: string;
  user_id: string;
  userconfigs: UserConfig[];
  corporate: boolean;
  is_independent: boolean;
  active_plan_subscription?: ActivePlanSubscription | null;
  active_add_on_subscriptions?: ActiveAddOnSubscription[];
  admin_overrides?: AdminOverrides | null;
}

export interface APIRobotInfo {
  robot_type: RobotType;
  ai_type: AIType;
  ai_tip?: string;
  ai_status: AIStatus;
  discrible: string;
  id: string;
  name: string;
  prompt: string;
  questions: QuestionParam[];
  user: UserInfo;
  ai_model: AIModel;
  urls?: RobotInfoUrl[];
  file_count: number;
  created_at: string;
  configs: RobotResponseConfigs;
  datasets: DatasetsInfo[];
  faq_count: number;
  all_file_count: number;
  all_faq_count: number;
  subject_name: string | null;
  permission_type?: 'OWNER' | 'EDIT' | 'VIEW';
  company_id?: string | null;
}

/** 单个机器人详情（与 RobotInfo 同义，供全局 import 统一使用） */
export type APIRobotInfo = RobotInfo;

export interface ConfigInfo {
  advanced_parser: { value: number; label: string; enabled: boolean }[];
  default_models: { value: string; label: string; enabled: boolean }[];
}

export interface GetRobotResponse {
  data: RobotInfo;
}

/**
 * 获取机器人基础信息
 * @param params
 * @returns
 */
export const getRobot = (params: GetRobotParams): Promise<GetRobotResponse> => {
  const { aiId } = params;
  const { get } = Http.use();
  return get(`/robots/${aiId}`);
};

export interface CreateRobotParams {
  name: string;
}

/**
 * 创建机器人
 * @param params
 * @returns
 */
export const createRobot = (params: CreateRobotParams) => {
  const { ...rest } = params;
  const { post } = Http.use();
  return post(`/robots`, rest);
};

export interface DeleteRobotParams {
  aiId: string;
  deleteDataset: boolean;
}

/**
 * 删除机器人
 * @param params
 * @returns
 */
export const deleteRobot = (params: DeleteRobotParams) => {
  const { aiId, deleteDataset } = params;
  const { delete: deleteFn } = Http.use();
  return deleteFn(`/robots/${aiId}`, {
    params: {
      delete_dataset: deleteDataset,
    },
  });
};

export interface UpdateRobotParams {
  aiId: string;
  name: string;
  subject_name?: string;
  discrible: string;
  ai_type: AIType;
  robot_type: RobotType;
  prompt: string;
  embedding_model_name?: string;
}

/**
 * 更新机器人信息
 * @param params
 * @returns
 */
export const updateRobot = (params: UpdateRobotParams) => {
  const { aiId, ...rest } = params;
  const { patch } = Http.use();
  return patch(`/robots/${aiId}`, rest);
};

// 后端接口返回的是字符串类型的 True 和 False
// 在后端改之前，前端先定义一个类型
export type FakeBoolean = 'True' | 'False';

/** 分享链接追加的一组 query 参数 */
export interface ShareParamLink {
  key: string;
  value: string;
}

export interface RobotBaseConfigs {
  source_lang?: string;
  widget_configs?: string;
  max_questions?: number | string;
  openai_model?: string;
  web_search_site_list?: string[];
  // 性格参数，语气
  talking_style?: string;
  // 回答创造性，对应 OpenAI 的 temperature 参数
  chat_temperature?: number;
  // 机器人无法回答时的话术，置空表示使用默认话术
  unknown_text?: string;
  // 数字人bot使用的prompt
  digital_human_prompt?: string;
  // 语音助手使用的prompt
  voice_agent_prompt?: string;
  // bot访问权限是否开启
  enable_access_control?: boolean;
  access_control_allow_email_list?: string[];
  input_user_identifier?: boolean;
  // Canvas类型：'1'=General Canvas, '2'=Map Canvas, ''=未选中
  canvas_type?: string;
  // 禁用第三方URL访问
  disable_third_party_url_access?: boolean;
  // Realtime 访问权限控制
  enable_realtime_access_control?: boolean;
  // Realtime 允许访问的邮箱列表（支持完整邮箱和域名，如 @company.com）
  realtime_access_control_allow_email_list?: string[];
  // Realtime Model: 'gemini' | 'openai'（历史命名，实际存 provider）
  realtime_model?: string;
  // Gemini Live 模型版本: 'gemini-3.1-flash-live-preview'(默认) | 'gemini-3.8-live'。缺失 = 3.1；realtime 服务按此选模型
  gemini_realtime_model?: string;
  // OpenAI Realtime 模型版本: 'gpt-realtime' | 'gpt-realtime-2' | 'gpt-live-1'（后者走 Live API，见 useOpenAILive）
  openai_realtime_model?: string;
  // gpt-live-1 声音模型提示词（人设/说话方式/发音/语言）。业务规则仍在 voice_agent_prompt；空 = realtime 服务内置模板
  openai_live_voice_prompt?: string;
  // OpenAI Realtime reasoning effort (仅 gpt-realtime-2 起支持): minimal/low/medium/high/xhigh
  openai_realtime_reasoning_effort?: string;
  // OpenAI Realtime 工具编排模式: 'classic'(taskExecutor 委派 catalog-agent) | 'direct'(voice agent 直连 MCP，单 Agent)，缺省 classic
  openai_realtime_tool_mode?: string;
  // Realtime 応答言語モード（OpenAI 線のみ）: 'fixed'(画面で選択した言語で固定，缺省) | 'auto'(各ターンのユーザー言語に合わせる)
  realtime_language_mode?: string;
  // Gemini 思考程度: minimal/low/medium/high
  voice_thinking_level?: string;
  // Gemini 工具响应模式: classic/stream
  voice_tool_mode?: string;
  // 聊天页是否显示 realtime 入口按钮，默认关闭
  show_realtime_in_share?: boolean;
  // 各 realtime provider 默认音色，未配置的 provider 使用前端内置默认值
  realtime_default_voices?: Partial<Record<RealtimeProvider, string>>;
  // 是否开启 Realtime 使用前同意声明（默认关闭，向后兼容现有 bot）
  enable_realtime_consent?: boolean;
  // Realtime 打开时是否要求输入昵称（默认开启，向后兼容；与 chat 的 input_user_identifier 对应）
  realtime_input_user_identifier?: boolean;
  // 是否允许用户在 Realtime 页临时设置会话级 prompt（仅本次通话生效、不落库；默认关闭）
  // 后端已注册 AIConfigType.ENABLE_REALTIME_SESSION_PROMPT
  enable_realtime_session_prompt?: boolean;
  // 是否开启 Chat 送信前同意声明（默认关闭，向后兼容现有 bot）
  // 后端已注册 AIConfigType.ENABLE_CHAT_CONSENT（robot_config 表白名单）
  enable_chat_consent?: boolean;
  // 分享链接追加参数列表（如楼层指南用 floor=3F），用于批量生成带参数的 URL 与二维码
  // 需后端注册 AIConfigType.SHARE_PARAM_LINKS，以 JSON 数组存储
  share_param_links?: ShareParamLink[];
  // Agent 是否启用思考过程
  enable_thinking?: boolean;
  // Agent 是否启用记忆功能
  enable_memory?: boolean;
  // ===== AI 电话受付 (Twilio) per-bot 行为配置；秘钥在 twilio_configs 表（応用連携配置）=====
  // 是否开通电话受付，默认 false（opt-in，向后兼容）
  phone_enabled?: boolean;
  // 电话专用 prompt（独立于文字 chat prompt）
  phone_prompt?: string;
  // 招呼语，可空
  phone_greeting?: string;
  // 判别不出时默认语言，默认 'ja'
  phone_language_default?: string;
  // 告别语补充指示，可空
  phone_farewell?: string;
  // 营业引导邮箱
  phone_sales_email?: string;
  // 音声モデル: 'gpt-realtime-2'(默认) / 'gpt-live-1'(全二重)。缺失 = gpt-realtime-2
  phone_model?: string;
  // gpt-live-1 音声モデル用プロンプト（人物像/発音/言語/相づち）。業務ルールは phone_prompt。空 = realtime 侧内置模板
  phone_live_voice_prompt?: string;
  // 声音，默认 'marin'
  phone_voice?: string;
  // 推理强度: minimal/low/medium
  phone_effort?: string;
  // 着信時动作: 'ai'(AI应答) / 'transfer'(转人工) / 'interpreter'(同時通訳)
  phone_call_mode?: 'ai' | 'transfer' | 'interpreter';
  // VAD 配置
  phone_vad?: { type?: string; eagerness?: string };
  // 工具开关
  phone_tools?: {
    knowledge?: { enabled?: boolean; top_k?: number };
    end_call?: { enabled?: boolean };
    transfer?: { enabled?: boolean; number?: string };
    booking?: { enabled?: boolean };
  };
  // 是否录音，默认 false
  phone_recording?: boolean;
  // 后处理（通话后调本 bot catalog-agent）；presets=预制开关，instruction=自定义提示词
  phone_post_call?: { enabled?: boolean; presets?: { lark_notify?: boolean }; instruction?: string };
  // 高级（可空覆盖各超时）
  phone_advanced?: Record<string, unknown>;
  // 转人工意图路由（opt-in，默认关/空路由 → 不影响未配置的 bot）
  phone_transfer?: {
    enabled?: boolean;
    announcement?: string; // 转送前案内模板，{部署名} 自动填充
    routes?: Array<{
      id?: string;
      label: string; // 转接给（负责人/部门名）
      number: string; // 直拨号码 E.164
      intent_examples?: string[]; // 关键词（可选，给模型判定提示）
      description?: string; // 什么情况下转给TA（自由文本，可选）
      enabled?: boolean;
    }>;
    default_route?: { label: string; number: string; enabled?: boolean } | null; // 不匹配兜底（带启用开关）
    caller_id?: string; // 外呼显示号，默认用本 bot 的 Twilio 号
    no_answer_message?: string; // 无人接听兜底语
  };
  // 同時通訳（双腿翻译桥，opt-in 默认关；phone_call_mode='interpreter' 时生效）
  phone_interpreter?: {
    enabled?: boolean;
    agent_number?: string; // 坐席电话 E.164
    agent_language?: string; // 坐席语言（translate 模型 13 语输出白名单）
    no_answer_message?: string; // 坐席无応答时播给客户的话
    customer_language_candidates?: string[]; // 客户语言候选（空=全部）
    customer_language_default?: string; // 客户语言无法判定时的兜底（判定前假定语言）
    greeting_language?: string; // 开场白播报语言（缺省回退 customer_language_default）
    voice_to_customer?: string; // 预留：translate 端点暂无 voice 字段
    voice_to_agent?: string; // 预留
    original_audio?: { enabled?: boolean; volume?: number }; // 原声垫底
    greeting?: string; // 开场白（AI 翻译+录音告知）
    verbatim_entities?: boolean; // 预留：数字/专名原文策略
    subtitle?: { enabled?: boolean }; // 二期预留（v1 UI 不露出）
  };
}

interface RobotResponseConfigs extends RobotBaseConfigs {
  questions_start_date?: string;
  questions_end_date?: string;
  // 当RAG检索到的知识没有答案时，允许 GPT 可以使用自身知识回答，即：像 ChatGPT 一样回答
  fallback_to_chatgpt?: FakeBoolean;
  final_question_use_gpt4?: FakeBoolean;
  recommend?: FakeBoolean;
  notification_admin?: FakeBoolean;
  faq_search_answer_with_embedding?: FakeBoolean;
  enable_images?: FakeBoolean;
  translate_question?: FakeBoolean;
  enable_web_search?: FakeBoolean;
  feedback_form?: TransferToHumanRequestConfigs;
  // 控制预览面板的接口地址
  preview_panel_api?: string;
  enable_tts?: boolean;
  enable_2d_digital_person?: boolean;
  qa_recommendation?: boolean;
  disable_third_party_url_access?: boolean;
  // 是否显示LLM服务的指标信息
  enable_llm_metrics?: boolean;
  client_installed?: string;
}

export interface TransferToHumanConfigs {
  content?: string;
  forward_chat_history?: boolean;
  forward_chat_history_visible_text?: Record<string, string>;
  forward_chat_history_ai_instruction?: string;
}

export interface TransferToHumanFromConfigs {
  content: string;
  forward_chat_history?: boolean;
  forward_chat_history_visible_text?: string;
  forward_chat_history_ai_instruction?: string;
}

// 接口请求的Base类型，和 RobotResponseConfigs 类型相比的差异是，布尔类型是 boolean 类型，而不是 FakeBoolean 类型
export interface RobotRequestBaseConfigs extends RobotBaseConfigs {
  fallback_to_chatgpt: boolean;
  recommend: boolean;
  notification_admin: boolean;
  faq_search_answer_with_embedding: boolean;
  enable_images: boolean;
  enable_web_search: boolean;
  preview_panel_api: string;
  enable_tts: boolean;
  enable_2d_digital_person: boolean;
  qa_recommendation: boolean;
  disable_third_party_url_access: boolean;
  enable_llm_metrics: boolean;
  enable_thinking: boolean;
  enable_memory: boolean;
}

// 针对请求接口的类型
export interface RobotRequestConfigs extends RobotRequestBaseConfigs {
  questions_start_date?: string;
  questions_end_date?: string;
  feedback_form?: TransferToHumanConfigs;
}

// 针对表单字段的类型
export interface RobotRequestFormConfigs extends RobotRequestBaseConfigs {
  question_date: (Date | string)[];
  feedback_form: TransferToHumanFromConfigs;
}

/**
 * 更新机器人的配置信息
 * @param params
 * @returns
 */
export const updateRobotConfigs = (
  params: { aiId: string; configs: RobotBaseConfigs },
  options?: { skipErrorNotification?: boolean }
) => {
  const { aiId, configs } = params;
  const { post } = Http.use();
  return post(`/robots/${aiId}/configs`, configs, options);
};

export interface GetRobotsByTypeParams extends PageQuery {
  ai_type: AIType;
}

/**
 * 根据类型获取机器人列表
 * @param params
 * @returns
 */
export const getRobotsByType = (params: GetRobotsByTypeParams) => {
  const { ai_type, ...rest } = params;
  const { get } = Http.use();
  return get(`/robots/anonymous`, { params: { ai_type: ai_type, rest } });
};

/**
 * 判断用户是否是机器人的协作者
 * @param params
 * @returns
 */
export const isRobotCollaborator = (params: { aiId: string }) => {
  const { aiId } = params;
  const { get } = Http.use();
  return get(`/robots/${aiId}/is_collaborator`);
};

interface CanvasWebhookData {
  canvas_url: string;
  show_popup: boolean;
  title: string;
}

/**
 * 调用预览面板的 Webhook，来判断是否需要展示预览面板，以及面板的url
 * @param params 用户信息参数
 * @returns Promise
 */
export const callCanvasWebhook = (url: string, params: RobotInfo & { question: string; answer: string }) => {
  requireSharedIntegration('Canvas');
  const { ...rest } = params;
  const { post } = Http.use();
  return post<CanvasWebhookData>(url, rest, {
    // 添加自定义配置，跳过错误提示
    skipErrorNotification: true,
  });
};

/**
 * 更改消息数据，设置预览面板url
 * @param params
 * @returns
 */
export const updateMessageCanvasUrl = ({
  aiId,
  messageId,
  sessionId,
  title,
  url,
}: {
  aiId: string;
  messageId: string;
  url: string;
  title: string;
  sessionId: string;
}) => {
  const { patch } = Http.use();
  return patch(`/v1/${aiId}/message_canvas?session_id=${sessionId}&message_id=${messageId}`, { url, title });
};

/**
 * 获取配置信息
 * @returns
 */
export const getConfig = () => {
  const { get } = Http.use();
  return get(`/configs`);
};

// Canvas图片处理相关接口
export interface ProcessImagesParams {
  ai_id: string;
  files: File[];
}

export interface ImageProcessResult {
  id: number;
  ai_id: string;
  path: string;
  width: number;
  height: number;
  original_filename: string;
  s3_filename: string;
  analyse_status: number;
  created_at: string;
}

export interface ProcessImagesResponse {
  status: string;
  ai_id: string;
  processed_count: number;
  results: ImageProcessResult[];
}

/**
 * 处理上传的图片，进行店铺识别
 * @param params
 * @returns
 */
export const processImages = (params: ProcessImagesParams): Promise<ProcessImagesResponse> => {
  requireSharedIntegration('Canvas');
  const { ai_id, files } = params;
  const { post } = Http.use();

  const formData = new FormData();
  formData.append('ai_id', ai_id);
  files.forEach((file) => {
    formData.append('files', file);
  });
  console.log(import.meta.env.VITE_CANVAS_API_BASE_URL);
  return post(import.meta.env.VITE_CANVAS_API_BASE_URL + '/process-images', formData);
};

export interface StoreAnalyseData {
  id: string;
  name: string;
  x: number;
  y: number;
}

export interface GetImageResponse {
  id: number;
  ai_id: string;
  path: string;
  image_width: number;
  image_height: number;
  origin_filename: string;
  s3_filename: string;
  analyse_status: number;
  analyse_status_text: string;
  analyse_data: StoreAnalyseData[];
  created_at: string;
  updated_at: string;
}

/**
 * 获取图片详情和店铺识别结果
 * @param params
 * @returns
 */
export const getImage = (params: { image_id: number; ai_id: string }): Promise<GetImageResponse> => {
  requireSharedIntegration('Canvas');
  const { image_id, ai_id } = params;
  const { get } = Http.use();
  console.log(import.meta.env.VITE_CANVAS_API_BASE_URL);
  return get(`${import.meta.env.VITE_CANVAS_API_BASE_URL}/get-image/${image_id}`, {
    params: { ai_id },
  });
};

export interface CanvasImageItem {
  id: number;
  path: string;
}

export interface GetCanvasImagesResponse {
  status: string;
  ai_id: string;
  count: number;
  images: CanvasImageItem[];
}

/**
 * 根据botId获取Canvas图片上传信息
 * @param params
 * @returns
 */
export const getCanvasImages = (params: { bot_id: string }): Promise<GetCanvasImagesResponse> => {
  requireSharedIntegration('Canvas');
  const { bot_id } = params;
  const { get } = Http.use();
  return get(`${import.meta.env.VITE_CANVAS_API_BASE_URL}/get-images/${bot_id}`);
};

export interface DeleteCanvasImageResponse {
  status: string;
  message: string;
}

/**
 * 删除Canvas图片
 * @param params
 * @returns
 */
export const deleteCanvasImage = (params: { image_id: number; ai_id: string }): Promise<DeleteCanvasImageResponse> => {
  requireSharedIntegration('Canvas');
  const { image_id, ai_id } = params;
  const { delete: deleteFn } = Http.use();
  return deleteFn(`${import.meta.env.VITE_CANVAS_API_BASE_URL}/delete-image/${image_id}`, {
    params: { ai_id },
  });
};

export interface SaveStoreInfoResponse {
  status: string;
  message: string;
}

/**
 * 保存店铺信息
 * @param params
 * @returns
 */
export const saveStoreInfo = (params: {
  image_id: number;
  ai_id: string;
  store_data: string; // JSON字符串形式的店铺数据
}): Promise<SaveStoreInfoResponse> => {
  requireSharedIntegration('Canvas');
  const { image_id, ai_id, store_data } = params;
  const { post } = Http.use();
  return post(`${import.meta.env.VITE_CANVAS_API_BASE_URL}/save-info`, {
    image_id,
    ai_id,
    store_data,
  });
};

export type BatchTransferRobotParams = {
  bot_ids: string[];
  // 移除协作者的访问权限
  clear_collaborators: boolean;
  // 强制转移知识库的所有者。
  // 对于某个知识库同时关联的多个Bot的场景，也将所有者转移走，那么对于其他还在当前账号的Bot，就只对该知识库有使用权，没有管理权了。
  force_transfer_dataset: boolean;
} & ({ user_id: string } | { email: string });

interface TransferredBot {
  bot_id: string;
  transferred_datasets: string[];
  skipped_datasets: string[];
}

interface BatchTransferRobotResponse {
  msg: string;
  transferred_bots: TransferredBot[];
  failed_bots: string[];
}

/**
 * 批量转让机器人所有权
 * @param params
 * @returns
 */
export const batchTransferRobots = (params: BatchTransferRobotParams): Promise<BatchTransferRobotResponse> => {
  const { post } = Http.use();
  return post(`/robots/transfer`, params);
};

export type DatasetCloneMode = 'share' | 'clone' | 'skip';
export type CloneTaskStatus = 'pending' | 'running' | 'success' | 'failed';
export type CloneSubtaskKind = 'base_config' | 'dataset_share' | 'dataset_clone' | 'chat_history';

export interface DatasetCloneOption {
  dataset_id: string;
  mode: DatasetCloneMode;
}

export interface CloneRobotPreviewDataset {
  id: string;
  name: string;
  documents_count: number;
  websites_count: number;
  faqs_count: number;
  integrations_count: number;
}

export interface CloneRobotPreviewResponse {
  name: string;
  suggested_new_name: string;
  datasets: CloneRobotPreviewDataset[];
  chat_history_summary: { sessions_count: number; questions_count: number };
}

export interface CloneRobotParams {
  aiId: string;
  name?: string;
  include_chat_history?: boolean;
  datasets: DatasetCloneOption[];
}

export interface CloneRobotResponse {
  new_robot_id: string;
  task_id: string;
  immediate_done: boolean;
}

export interface CloneSubtask {
  kind: CloneSubtaskKind;
  label: string;
  status: CloneTaskStatus;
  progress: number;
  error?: string | null;
}

export interface CloneTaskResponse {
  id: string;
  source_robot_id: string;
  target_robot_id?: string | null;
  new_robot_name: string;
  status: CloneTaskStatus;
  progress_percent: number;
  subtasks: CloneSubtask[];
  retry_count: number;
  failed_reason?: string | null;
  can_retry: boolean;
  created_at: string;
  updated_at: string;
}

/** 复制弹窗打开时拉取数据集 / 聊天历史摘要 */
export const getCloneRobotPreview = async (aiId: string): Promise<CloneRobotPreviewResponse> => {
  const { get } = Http.use();
  const response = await get<CloneRobotPreviewResponse>(`/robots/${aiId}/clone/preview`);
  return response.data;
};

/** 发起复制任务 */
export const cloneRobot = async (params: CloneRobotParams): Promise<CloneRobotResponse> => {
  const { aiId, ...body } = params;
  const { post } = Http.use();
  const response = await post<CloneRobotResponse>(`/robots/${aiId}/clone`, body);
  return response.data;
};

/** 获取某个 bot 的当前/最近一次复制任务（用于卡片徽章点击打开进度弹窗） */
export const getActiveCloneTask = async (aiId: string): Promise<CloneTaskResponse> => {
  const { get } = Http.use();
  const response = await get<CloneTaskResponse>(`/robots/${aiId}/clone-task`);
  return response.data;
};

/** 复制进度详情（弹窗轮询） */
export const getCloneTask = async (taskId: string): Promise<CloneTaskResponse> => {
  const { get } = Http.use();
  const response = await get<CloneTaskResponse>(`/robots/clone-tasks/${taskId}`);
  return response.data;
};

/** 整任务重试 */
export const retryCloneTask = async (taskId: string): Promise<CloneTaskResponse> => {
  const { post } = Http.use();
  const response = await post<CloneTaskResponse>(`/robots/clone-tasks/${taskId}/retry`);
  return response.data;
};

export type DonateDatasetStrategy = 'not_transfer' | 'transfer' | 'clone_then_transfer';
export type DonateBotStrategy = 'move' | 'copy';

export interface DonatePreviewDataset {
  dataset_id: string;
  dataset_name: string;
  linked_bot_ids: string[];
  linked_bot_names: string[];
  shared: boolean;
}

export interface DonatePreviewResponse {
  target_company_id: string | null;
  already_in_organization: boolean;
  datasets: DonatePreviewDataset[];
}

export interface DonateBotParams {
  target_company_id?: string;
  dataset_strategy: DonateDatasetStrategy;
  /** 'move'（默认）搬走原 bot；'copy' 克隆 bot 到企业，原 bot 留个人 */
  bot_strategy?: DonateBotStrategy;
  clear_collaborators?: boolean;
}

export interface DonatedBotInfo {
  mode: DonateBotStrategy;
  bot_id: string;
  /** copy 模式下返回:企业里新克隆出的 bot id + 克隆任务 id */
  new_bot_id?: string;
  task_id?: string;
  previous_company_id: string | null;
  new_company_id: string;
  dataset_strategy: DonateDatasetStrategy;
  transferred_datasets: string[];
  cloned_datasets: { src_dataset_id: string; new_dataset_id: string }[];
}

export interface DonateBotResponse {
  msg: string;
  donated_bot: DonatedBotInfo;
}

/**
 * 获取让渡到企业前的检查信息（哪些 dataset 被其他 bot 共用）
 */
export const previewBotDonation = async (botId: string): Promise<DonatePreviewResponse> => {
  const { get } = Http.use();
  const response = await get<DonatePreviewResponse>(`/robots/${botId}/donate_preview`);
  return response.data;
};

/**
 * 将个人 bot 让渡到当前企业
 */
export const donateBotToEnterprise = async (botId: string, params: DonateBotParams): Promise<DonateBotResponse> => {
  const { post } = Http.use();
  const response = await post<DonateBotResponse>(`/robots/${botId}/donate_to_enterprise`, params);
  return response.data;
};
